import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';

const agentLabel = (agent) =>
  agent.name || `${agent.first_name || ''} ${agent.last_name || ''}`.trim() || agent.email || 'Unknown Agent';

/**
 * Checkbox-panel multi-select for picking secondary agents on an Open Home.
 * No multi-select component exists elsewhere in the codebase (agent pickers
 * are all single-select <select>s) - this is a small, purpose-built one:
 * click the field to open a checkbox panel, selected agents show as chips.
 */
export default function AgentMultiSelect({ agents = [], value = [], onChange, excludeIds = [], placeholder = 'Click and choose multiple agents' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const excluded = new Set(excludeIds.map(String));
  const selectable = agents.filter((a) => !excluded.has(String(a.id)));
  const selectedIds = value.map(String);

  const toggle = (id) => {
    const idStr = String(id);
    onChange(selectedIds.includes(idStr)
      ? value.filter((v) => String(v) !== idStr)
      : [...value, id]);
  };

  const remove = (id) => onChange(value.filter((v) => String(v) !== String(id)));

  return (
    <div ref={ref} className="relative">
      <div
        onClick={() => setOpen((o) => !o)}
        className="w-full min-h-[38px] border border-gray-300 rounded-md px-2.5 py-1.5 text-sm cursor-pointer flex items-center gap-1.5 flex-wrap"
      >
        {selectedIds.length === 0 && <span className="text-gray-400">{placeholder}</span>}
        {selectedIds.map((id) => {
          const agent = agents.find((a) => String(a.id) === id);
          return (
            <span key={id} className="inline-flex items-center gap-1 bg-teal-50 text-teal-700 text-xs rounded-full pl-2 pr-1 py-0.5">
              {agent ? agentLabel(agent) : `#${id}`}
              <button type="button" onClick={(e) => { e.stopPropagation(); remove(id); }} className="hover:text-teal-900">
                <X className="w-3 h-3" />
              </button>
            </span>
          );
        })}
        <ChevronDown className="w-3.5 h-3.5 text-gray-400 ml-auto flex-shrink-0" />
      </div>
      {open && (
        <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border rounded-lg shadow-lg max-h-52 overflow-y-auto">
          {selectable.length === 0 && (
            <div className="px-3 py-2 text-xs text-gray-400">No other agents available</div>
          )}
          {selectable.map((agent) => (
            <label key={agent.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={selectedIds.includes(String(agent.id))}
                onChange={() => toggle(agent.id)}
              />
              {agentLabel(agent)}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
